import { produce } from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';

import baseEntity from './base.entity';
import { storeName as floorEntityStore } from './floor.entity';
import { storeName as projectEntityStore } from './project.entity';
import { storeName as projectItemEntityStore } from './projectItem.entity';
import { RootModel } from '../models';
import {
  getInventoryProducts,
  getBySharedCode,
  shareFilterResult,
  updatePrice,
  updateStatus,
} from '../../services/inventory-product.service';
import {
  convertProjectChildItemResponse,
  convertPropertiesResponse,
} from '../../utils/convertUtils';
import {
  PaginationResponseTypes,
  PropertiesResponseTypes,
  ResponseTypes,
} from '../../types/response';
import { fetchProjectChildItemsCms } from '../../services/projectChildItem';
import { ProjectChildItemTypes } from '../../types';

export const storeName = 'projectChildItemEntity';

export const projectChildItemEntity = createModel<RootModel>()({
  name: storeName,
  state: { codes: {} },
  reducers: {
    ...baseEntity,
    saveInventoryCodes: produce((state, entities) => {
      (entities || []).forEach((entity: any) => {
        // overwrite old entity with new entity, keep old field of entity (activeSession)
        const newEntity = {
          ...entity,
          ...state.codes[entity.productCode],
        };
        state.codes[entity.productCode] = newEntity;
      });
    }),
  },
  effects: (dispatch: RematchDispatch<typeof projectChildItemEntity>) => ({
    async fetchInventory(payload) {
      const resp: PaginationResponseTypes<PropertiesResponseTypes[]> = await getInventoryProducts(
        payload
      );
      if (!resp) return null;

      const { data } = resp;

      const projectChildItems = data.content.map((i) => {
        return convertProjectChildItemResponse(i);
      });

      const result = convertPropertiesResponse(data.content);
      if (!result) return null;

      if (result.projects && !!result.projects.length) {
        dispatch[projectEntityStore].saves(result.projects);
      }

      if (result.projects && !!result.projectItems.length) {
        dispatch[projectItemEntityStore].saves(result.projectItems);
      }

      if (result.projects && !!result.floors.length) {
        dispatch[floorEntityStore].saves(result.floors);
      }

      this.saves(projectChildItems);
      return resp;
    },
    async shareFilterResult(payload) {
      const resp: { data: string } = await shareFilterResult(payload);
      return resp ? resp.data : null;
    },
    async fetchInventoryBySharedCode(payload) {
      const resp: PaginationResponseTypes<PropertiesResponseTypes[]> = await getBySharedCode(
        payload
      );
      if (!resp) return null;

      const { data } = resp;

      const projectChildItems = data.content.map((i) => {
        return convertProjectChildItemResponse(i);
      });

      const result = convertPropertiesResponse(data.content);
      if (!result) return null;

      if (result.projects && !!result.projects.length) {
        dispatch[projectEntityStore].saves(result.projects);
      }

      if (result.projects && !!result.projectItems.length) {
        dispatch[projectItemEntityStore].saves(result.projectItems);
      }

      if (result.projects && !!result.floors.length) {
        dispatch[floorEntityStore].saves(result.floors);
      }

      this.saves(projectChildItems);
      return resp;
    },
    async updatePrice(payload) {
      try {
        const { data }: ResponseTypes<PropertiesResponseTypes> = await updatePrice(
          payload.code,
          payload
        );
        this.saves([convertProjectChildItemResponse(data)]);
        return data;
      } catch (e) {
        throw new Error(e);
      }
    },

    async updateStatus(payload) {
      try {
        const { data }: ResponseTypes<PropertiesResponseTypes> = await updateStatus(
          payload.code,
          payload
        );
        this.saves([convertProjectChildItemResponse(data)]);
        return data;
      } catch (e) {
        throw new Error(e);
      }
    },

    async fetchProjectChildItem(payload) {
      const resp: PaginationResponseTypes<ProjectChildItemTypes[]> =
        await fetchProjectChildItemsCms(payload);
      if (!resp) return null;
      const { data } = resp;
      this.saveInventoryCodes(data.content);
      return resp;
    },
  }),
});

export default projectChildItemEntity;
